/**
 * PDF внизу поста (docs/release-2026-09-19.md §9.2): «Прикрепить PDF…», перетаскивание на
 * полосу «Файл» и «Удалить файл» — ОДНО состояние на все три входа.
 *
 * ПОЧЕМУ ХУК ЭКРАНА, А НЕ ПОЛОСЫ. Так же, как у картинки (`visual/useVisualUpload.ts`): полосу
 * пересоздаёт смена режима экрана (чтение / правка / сравнение), а загрузка файла в 20 МиБ на
 * медленном канале идёт десятки секунд и обязана это пережить. Владелец состояния один —
 * `DraftPanel`, полоса только рисует.
 *
 * ПУТЬ ЦЕЛИКОМ: проверки в браузере → `POST …/versions/:n/document` (multipart: поле
 * `expected_content_hash`, затем часть `file`) → НОВАЯ версия черновика → `onDone(version_no)`,
 * и экран переходит на неё. Документ входит в подписываемое содержание, поэтому прикрепление
 * и снятие — всегда новая версия: утверждённая без файла версия не получает его задним числом.
 *
 * ПОЧЕМУ ПРОВЕРКИ И В БРАУЗЕРЕ. Не вместо сервера, а раньше него: тело в 25 МиБ маршрут
 * оборвал бы, и человек увидел бы «соединение оборвалось» вместо «файл тяжелее 20 МБ», —
 * после полуминуты отправки. Потолок — `HUMAN_DOCUMENT_MAX_BYTES` из контрактов, не литерал.
 * И ни одна проверка здесь не строже сервера: PDF опознаётся по заявлению браузера, по имени
 * или по магии `%PDF-` — той же, что смотрит сервер (`isPdfBytes`); имя файла не проверяется
 * вовсе — сервер приводит его сам (путь, управляющие символы, пробелы по краям, `.pdf`, 128
 * знаков), а итоговое имя приходит в ответе, и итог называет именно его.
 *
 * ПОРЯДОК ПОЛЕЙ ФОРМЫ — хэш, потом файл. Потоковый разбор multipart видит поля, пришедшие ДО
 * файла, раньше, чем начнёт читать сам файл; обратный порядок заставил бы сервер держать
 * 20 МиБ, не зная, для той ли версии они пришли.
 */

import {
  DRAFT_TRANSITIONS,
  type DraftState,
  HUMAN_DOCUMENT_MAX_BYTES,
  isPdfBytes,
  PDF_MAGIC,
} from '@smm/contracts';
import { DocumentRemoveResponseSchema, DocumentUploadResponseSchema } from '@smm/contracts/schemas';
import {
  type ChangeEvent,
  type DragEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ApiRequestError, del, postForm } from '../../api/client.ts';
import { fileSize } from '../../components/format.ts';
import { FILE_OBJECT, lockReason } from '../versionLock.ts';
import { saveErrorText } from '../versionSave.ts';

export const DOCUMENT_ACCEPT = 'application/pdf';

/**
 * Состояния, из которых версия вытесняется новой, — выведены из рёбер контракта, как в
 * редакторе и в полосе визуала: прикрепление файла рождает v+1 и возможно ровно там же.
 */
const EDITABLE: ReadonlySet<DraftState> = new Set(
  DRAFT_TRANSITIONS.filter((t) => t.to === 'superseded').map((t) => t.from),
);

/** Причина словами (без точки) или `null` — версию можно заменить, и файл меняется. */
export function documentLockReason(status: DraftState): string | null {
  if (EDITABLE.has(status)) return null;
  return lockReason(status, FILE_OBJECT);
}

export interface UseDocumentUploadArgs {
  readonly draftId: string;
  readonly versionNo: number;
  /** Хэш показанной версии: сервер отобьёт файл, если версия успела смениться. */
  readonly contentHash: string;
  /**
   * Состояние показанной версии: файл меняется только у той, которую можно заменить, а отказ
   * называет причину по состоянию (`documentLockReason`). `null` — модель ещё не разобрана.
   */
  readonly status: DraftState | null;
  /**
   * Родилась новая версия (`version_no`) — экран переходит на неё; `null` — номер неизвестен
   * или экран устарел: перечитать черновик и показать текущую.
   */
  readonly onDone: (versionNo: number | null) => void;
  /**
   * Новой версии не появилось: загрузку отменили, тот же файл уже в версии, файла у версии уже
   * нет. Экран перечитывается, а набранная, но не сохранённая правка текста ОСТАЁТСЯ: она
   * по-прежнему правка показанной версии (ревью 19.09.2026, ui-2).
   */
  readonly onUnchanged: () => void;
}

/** Что сейчас идёт: отправка тела, сборка версии сервером, снятие файла. */
export type DocumentPhase = 'upload' | 'save' | 'remove';

export interface DocumentUpload {
  readonly dragging: boolean;
  readonly busy: boolean;
  readonly phase: DocumentPhase | null;
  /** Доля отправленного тела, 0…1; `null` — длина неизвестна или идёт не отправка. */
  readonly progress: number | null;
  /** Имя файла, который сейчас едет, — для строки хода. */
  readonly fileName: string | null;
  readonly error: string | null;
  readonly notice: string | null;
  /** Зона сброса — только у версии, файл которой можно менять. */
  readonly dropProps: {
    readonly onDragOver: (e: DragEvent<HTMLElement>) => void;
    readonly onDragLeave: (e: DragEvent<HTMLElement>) => void;
    readonly onDrop: (e: DragEvent<HTMLElement>) => void;
  };
  /**
   * Версию менять нельзя: зоны сброса нет (ни подсветки, ни «Отпустите PDF», ни загрузки), но
   * файл не проваливается и дальше — в зону картинки колонки поста, откуда пришёл бы отказ про
   * картинку. Сброшенный файл получает причину по состоянию версии.
   */
  readonly guardProps: {
    readonly onDragOver: (e: DragEvent<HTMLElement>) => void;
    readonly onDrop: (e: DragEvent<HTMLElement>) => void;
  };
  /** Клавиатурный путь: клик по скрытому `<input type="file">`. */
  readonly pick: () => void;
  readonly inputRef: RefObject<HTMLInputElement | null>;
  readonly onInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  readonly accept: string;
  readonly remove: () => void;
  /** Прервать отправку тела. После отмены экран перечитывается: сервер мог успеть. */
  readonly cancel: () => void;
}

/**
 * Проверки ДО сети, в которых браузер и сервер не расходятся: пустой файл и потолок 20 МиБ —
 * с числом. Возвращает отказ или `null`. Что это PDF, решает `isPdfFile` — ей может
 * понадобиться прочесть начало файла.
 */
export function documentRejection(file: File): string | null {
  if (file.size === 0) return `Файл «${file.name}» пустой — 0 байт.`;
  if (file.size > HUMAN_DOCUMENT_MAX_BYTES) {
    return `Файл ${fileSize(file.size)}, потолок — ${fileSize(HUMAN_DOCUMENT_MAX_BYTES)}. Сожмите PDF и попробуйте снова.`;
  }
  return null;
}

/**
 * Заявил ли себя файл PDF-ом: тип от браузера (он ставит его по расширению) или имя `.pdf`.
 * Ответ «да» здесь окончательный для браузера — магию всё равно сверит сервер.
 */
export const declaresPdf = (file: File): boolean =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

/**
 * PDF ли это — по заявлению, а если его нет (файл без расширения, чужой тип), то по магии
 * `%PDF-` в первых байтах: так смотрит и сервер (`isPdfBytes`), и отказ здесь не строже его.
 */
export async function isPdfFile(file: File): Promise<boolean> {
  if (declaresPdf(file)) return true;
  try {
    return isPdfBytes(new Uint8Array(await file.slice(0, PDF_MAGIC.length).arrayBuffer()));
  } catch {
    return false;
  }
}

export function notPdfText(file: File): string {
  const type = file.type === '' ? 'тип не определён' : `тип ${file.type}`;
  return `Это не PDF: у файла «${file.name}» ${type}, и в начале нет подписи %PDF-. Прикрепить к посту можно только PDF.`;
}

/**
 * Номер новой версии из ответа: схема загрузки или схема снятия (`DocumentRemoveResponseSchema`
 * — форма правки текста). Ни одна не разобрала — номер читается по полю, а нет и его — `null`:
 * версия, скорее всего, уже есть, и экран просто перечитывается, а не сообщает об отказе того,
 * что удалось.
 */
export function versionNoOf(raw: unknown): number | null {
  const parsed = DocumentUploadResponseSchema.safeParse(raw);
  if (parsed.success) return parsed.data.version_no;
  const removed = DocumentRemoveResponseSchema.safeParse(raw);
  if (removed.success) return removed.data.version_no;
  if (typeof raw !== 'object' || raw === null) return null;
  const n = (raw as Record<string, unknown>)['version_no'];
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : null;
}

const reasonOf = (e: ApiRequestError): string | null => {
  const d = e.details;
  if (typeof d !== 'object' || d === null) return null;
  const r = (d as Record<string, unknown>)['reason'];
  return typeof r === 'string' ? r : null;
};

/** Экран устарел: версия сменилась между показом и действием. */
export function isStaleVersion(e: unknown): boolean {
  if (!(e instanceof ApiRequestError) || e.status !== 409) return false;
  const reason = reasonOf(e);
  return reason === 'content_changed' || reason === 'stale_base_version';
}

/**
 * Отказы словами: три из четырёх человек устраняет сам, и «не удалось» их прятало бы.
 * `bytes` — размер отправленного файла (у снятия файла тела нет — `null`).
 */
export function documentErrorText(
  e: unknown,
  versionNo: number,
  bytes: number | null = null,
): string {
  if (!(e instanceof ApiRequestError)) return e instanceof Error ? e.message : String(e);
  if (isStaleVersion(e)) {
    return (
      `Пока вы выбирали файл, появилась версия новее v${String(versionNo)}: её сохранил кто-то ` +
      'другой или соседняя вкладка. Экран перезаполнен — повторите действие с файлом.'
    );
  }
  if (e.status === 413) {
    // Свой отказ бэкенда — с конвертом `payload_too_large`: тело больше потолка маршрута.
    if (e.code === 'payload_too_large') {
      return `Сервер не принял файл: он тяжелее ${fileSize(HUMAN_DOCUMENT_MAX_BYTES)}.`;
    }
    // Голый 413 без конверта отдаёт прокси ПЕРЕД сервером: его потолок тела ниже серверного
    // (край поднимают отдельно от выкладки — `deploy/edge/caddy-pdf-limit.sh`). Файл браузер
    // уже проверил и пропустил, так что «тяжелее 20 МБ» здесь было бы ложью (ревью, ui-7).
    const what = bytes === null ? 'запрос' : `файл ${fileSize(bytes)}`;
    return (
      `Прокси перед сервером не пропустил ${what} (HTTP 413) — до сервера он не дошёл. ` +
      'Сожмите PDF или попросите инженера поднять потолок прокси.'
    );
  }
  if (e.status === 415 || e.status === 422 || e.code === 'invalid_request') {
    return `Сервер не принял файл: ${e.message}.`;
  }
  return saveErrorText(e);
}

export function useDocumentUpload(a: UseDocumentUploadArgs): DocumentUpload {
  const { draftId, versionNo, contentHash, status, onDone, onUnchanged } = a;
  // Модель ещё не разобрана — полосы нет, и отказ сюда не доходит; причина — на всякий случай.
  const lock = status === null ? 'версия ещё не загрузилась' : documentLockReason(status);
  const editable = lock === null;
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<DocumentPhase | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const acRef = useRef<AbortController | null>(null);

  // Итог действия принадлежит СВОЕМУ черновику: фраза «файл прикреплён — v3», переехавшая на
  // соседний пост, утверждала бы про него то, чего не было. Правка во время рендера, а не в
  // эффекте: иначе один кадр показывал бы чужой итог.
  const [forDraft, setForDraft] = useState(draftId);
  if (forDraft !== draftId) {
    setForDraft(draftId);
    setError(null);
    setNotice(null);
  }

  /**
   * Экран ушёл (другой пост, другая страница) — загрузка отменяется, и её исход никому не
   * сообщается: `onDone` перечитал бы экран, которого уже нет. На другой пост экран уходит
   * размонтированием: `WorkspacePage` пересоздаёт панель черновика на каждый переход
   * (`key={draftId}`), иначе загрузка поста A доезжала бы до экрана поста B (ревью, ui-1).
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      acRef.current?.abort();
    };
  }, []);

  const finish = useCallback((): void => {
    if (!alive.current) return;
    setBusy(false);
    setPhase(null);
    setProgress(null);
    setFileName(null);
  }, []);

  /** Отказ действия; `bytes` — размер отправленного файла, у снятия файла — `null`. */
  const fail = useCallback(
    (e: unknown, bytes: number | null): void => {
      if (!alive.current) return;
      if (e instanceof ApiRequestError && e.code === 'aborted') {
        setNotice('Загрузка отменена — версия не создана.');
        // Отмена могла прийти, когда сервер уже собрал версию: перечитать дешевле, чем гадать.
        // Набранная правка остаётся — отмена её не касается.
        onUnchanged();
        return;
      }
      setError(documentErrorText(e, versionNo, bytes));
      if (isStaleVersion(e)) onDone(null);
    },
    [onDone, onUnchanged, versionNo],
  );

  /** Отправка проверенного файла: форма, ход, итог. */
  const send = useCallback(
    (file: File, multi: string): void => {
      acRef.current?.abort();
      const ac = new AbortController();
      acRef.current = ac;
      const form = new FormData();
      form.append('expected_content_hash', contentHash);
      form.append('file', file, file.name);
      setBusy(true);
      setPhase('upload');
      setProgress(0);
      setFileName(file.name === '' ? 'document.pdf' : file.name);
      postForm<unknown>(`/api/v1/drafts/${draftId}/versions/${String(versionNo)}/document`, form, {
        signal: ac.signal,
        onProgress: (p) => {
          if (ac.signal.aborted) return;
          setProgress(p.total === null || p.total === 0 ? null : Math.min(1, p.loaded / p.total));
        },
        onSent: () => {
          if (ac.signal.aborted) return;
          setPhase('save');
          setProgress(null);
        },
      })
        .then((raw) => {
          if (!alive.current) return;
          const n = versionNoOf(raw);
          // Имя — то, что сервер записал (он приводит имя сам), а не то, что выбрано на диске:
          // читатель канала увидит именно его.
          const parsed = DocumentUploadResponseSchema.safeParse(raw);
          const name = parsed.success
            ? parsed.data.file_name
            : file.name === ''
              ? 'document.pdf'
              : file.name;
          if (n !== null && n === versionNo) {
            // `200`: этот файл под этим именем уже в версии — повтор, а не правка.
            setNotice(
              `${multi}Файл «${name}» уже прикреплён к v${String(n)} — новой версии не появилось.`,
            );
            onUnchanged();
            return;
          }
          setNotice(
            `${multi}Файл «${name}» прикреплён${n === null ? '' : ` — сохранено как v${String(n)}`}. ` +
              'Пост уйдёт с ним, последним блоком.',
          );
          onDone(n);
        })
        .catch((e: unknown) => {
          fail(e, file.size);
        })
        .finally(() => {
          if (acRef.current === ac) acRef.current = null;
          finish();
        });
    },
    [contentHash, draftId, fail, finish, onDone, onUnchanged, versionNo],
  );

  const upload = useCallback(
    (files: readonly File[]): void => {
      if (busy) return;
      setError(null);
      setNotice(null);
      const file = files[0];
      if (file === undefined) return;
      if (!editable) {
        setError(`Файл к этой версии не прикрепить: ${lock ?? ''}.`);
        return;
      }
      const multi =
        files.length > 1
          ? `Взят первый файл из ${String(files.length)}: к посту крепится один. `
          : '';
      if (multi !== '') setNotice(multi.trim());
      const bad = documentRejection(file);
      if (bad !== null) {
        setError(bad);
        return;
      }
      if (declaresPdf(file)) {
        send(file, multi);
        return;
      }
      // Ни тип, ни имя не говорят «PDF» — решает начало файла, как у сервера. Пока читаем пять
      // байт, полоса занята: второй файл, брошенный в эти миллисекунды, не должен обогнать первый.
      setBusy(true);
      void isPdfFile(file).then((pdf) => {
        if (!alive.current) return;
        if (pdf) {
          send(file, multi);
          return;
        }
        setBusy(false);
        setError(notPdfText(file));
      });
    },
    [busy, editable, lock, send],
  );

  const remove = useCallback((): void => {
    if (busy) return;
    setError(null);
    setNotice(null);
    if (!editable) {
      setError(`Файл у этой версии не снять: ${lock ?? ''}.`);
      return;
    }
    setBusy(true);
    setPhase('remove');
    del<unknown>(
      `/api/v1/drafts/${draftId}/versions/${String(versionNo)}/document?expected_content_hash=${encodeURIComponent(contentHash)}`,
    )
      .then((raw) => {
        if (!alive.current) return;
        const n = versionNoOf(raw);
        if (n !== null && n === versionNo) {
          // `200`: файла у версии уже нет (снят в соседней вкладке) — новой версии не родилось.
          setNotice(`У v${String(n)} файла уже нет — новой версии не появилось.`);
          onUnchanged();
          return;
        }
        setNotice(
          `Файл убран${n === null ? '' : ` — сохранено как v${String(n)}`}: пост уйдёт без файла.`,
        );
        onDone(n);
      })
      .catch((e: unknown) => {
        fail(e, null);
      })
      .finally(finish);
  }, [busy, contentHash, draftId, editable, fail, finish, lock, onDone, onUnchanged, versionNo]);

  const cancel = useCallback((): void => {
    acRef.current?.abort();
  }, []);

  const pick = useCallback((): void => {
    inputRef.current?.click();
  }, []);

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const list = e.target.files;
      const files = list === null ? [] : [...list];
      // Без сброса второй выбор ТОГО ЖЕ файла не даёт `change` вовсе, и повтор после отказа
      // выглядел бы как зависшая кнопка.
      e.target.value = '';
      upload(files);
    },
    [upload],
  );

  /*
   * Зона сброса — САМА ПОЛОСА, и события дальше неё не идут. Полоса лежит внутри колонки
   * поста, а колонка — зона сброса КАРТИНКИ (`useVisualUpload`): без `stopPropagation` PDF,
   * отпущенный на полосу «Файл», ушёл бы ещё и в загрузку картинки и получил бы оттуда отказ
   * «это не картинка» рядом с успехом.
   */
  const onDragOver = useCallback((e: DragEvent<HTMLElement>): void => {
    // Без preventDefault браузер не породит `drop` вовсе.
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  }, []);

  const onDragLeave = useCallback((e: DragEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }, []);

  const onDrop = useCallback(
    (e: DragEvent<HTMLElement>): void => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      // jsdom не реализует DataTransfer: тест передаёт литерал в init события.
      const dt = e.dataTransfer as DataTransfer | null;
      const list = dt?.files ?? null;
      upload(list === null ? [] : [...list]);
    },
    [upload],
  );

  /*
   * Страж полосы неправимой версии. Не зона сброса — подсветки и загрузки нет, — но событие
   * дальше полосы не идёт по той же причине, что выше: колонка ответила бы отказом про
   * картинку. `preventDefault` на `dragover` обязателен и здесь: иначе браузер сам откроет
   * отпущенный PDF во вкладке, уведя человека с экрана.
   */
  const onGuardOver = useCallback((e: DragEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onGuardDrop = useCallback(
    (e: DragEvent<HTMLElement>): void => {
      e.preventDefault();
      e.stopPropagation();
      if (busy) return;
      setNotice(null);
      setError(`Файл к этой версии не прикрепить: ${lock ?? ''}.`);
    },
    [busy, lock],
  );

  return {
    dragging,
    busy,
    phase,
    progress,
    fileName,
    error,
    notice,
    dropProps: { onDragOver, onDragLeave, onDrop },
    guardProps: { onDragOver: onGuardOver, onDrop: onGuardDrop },
    pick,
    inputRef,
    onInputChange,
    accept: DOCUMENT_ACCEPT,
    remove,
    cancel,
  };
}
